/**
 * Hooks for pane routing functionality
 */

import { paneTypeFor } from "@/analytics/routeName";
import { ROUTES } from "@/constants/routes";
import {
  usePaneRouterContext,
  usePaneRouterContextSafe,
} from "@/context/PaneRouterContext";
import type {
  LocationState,
  PaneKey,
  PaneLocation,
  PaneNavigateParams,
} from "@/types/pane-routing";
import { trackPaneClosed, trackPaneOpened } from "@/utils/analytics";
import { useCallback } from "react";
import {
  matchPath,
  useLocation,
  useNavigate,
  useParams,
  type Params,
} from "react-router-dom";
import { useBreakpoint } from "./useBreakpoint";

/**
 * Enhanced navigate hook that supports pane-specific navigation
 */
export function useAppNavigate() {
  const navigate = useNavigate();
  const location = useLocation();

  const md = useBreakpoint("md");

  return useCallback(
    ({ pane, to, options = {} }: PaneNavigateParams) => {
      const currentState = (location.state as LocationState) || {};

      if (!md) {
        if (to === null) {
          // Below md the pane renders as a full route — the current pathname
          // IS the pane location.
          trackPaneClosed({
            paneType: paneTypeFor(location.pathname),
            method: "close",
          });
          navigate(-1);
          return;
        }

        trackPaneOpened({
          paneType: paneTypeFor(to),
          method: options.replace ? "replace" : "push",
          source: options.analyticsSource,
          layout: "full_route",
        });
        navigate(to, {
          replace: options.replace || false,
          state: options.state,
        });
        return;
      }

      // Closing the pane clears its location and navigation history.
      if (to === null) {
        trackPaneClosed({
          paneType: paneTypeFor(currentState[pane]?.pathname),
          method: "close",
        });
        const newState: LocationState = { ...currentState };
        delete newState[pane];
        delete newState.rightPaneHistory;

        navigate(location.pathname + location.search, {
          replace: options.replace || false,
          state: newState,
          preventScrollReset: options.preventScrollReset,
        });
        return;
      }

      trackPaneOpened({
        paneType: paneTypeFor(to),
        method: options.replace ? "replace" : "push",
        source: options.analyticsSource,
        layout: "pane",
      });

      const newPaneLocation: PaneLocation = {
        pathname: to,
        search: "",
        hash: "",
        state: options.state,
        key: `pane-${Date.now()}-${Math.random()}`,
      };

      const currentPane = currentState[pane];
      const newState: LocationState = {
        ...currentState,
        [pane]: newPaneLocation,
      };

      if (!options.replace && currentPane?.pathname) {
        newState.rightPaneHistory = [
          ...(currentState.rightPaneHistory ?? []),
          { ...currentPane },
        ];
      }

      navigate(location.pathname + location.search, {
        replace: options.replace || false,
        state: newState,
        preventScrollReset: options.preventScrollReset,
      });
    },
    [navigate, location, md],
  );
}

/**
 * Get the current location for a specific pane
 */
export function usePaneLocation(pane?: PaneKey): PaneLocation | null {
  const location = useLocation();
  const paneContext = usePaneRouterContextSafe();

  // If called within a PaneRouter context and no pane specified, use context pane
  if (paneContext && !pane) {
    return paneContext.location;
  }

  // If pane specified, get from location state
  if (pane) {
    const state = (location.state as LocationState) || {};
    return state[pane] || null;
  }

  return null;
}

/**
 * Get the current pathname for a specific pane
 */
export function usePanePathname(pane?: PaneKey): string {
  const paneLocation = usePaneLocation(pane);
  return paneLocation?.pathname || "/";
}

/**
 * Get the current params for the pane route
 * @param pattern Optional route pattern to match against (e.g., "/user/:id")
 * If not provided, will try to get params from context (legacy behavior)
 */
export function usePaneParams(pattern?: string): Record<string, string> {
  const paneContext = usePaneRouterContextSafe();

  if (!paneContext) {
    return useParams() as Record<string, string>;
  }

  // If pattern is provided, extract params from current location
  if (pattern) {
    const { location } = paneContext;
    return extractParamsFromPath(location.pathname || "", pattern);
  }

  // Fallback to context params (legacy behavior)
  return paneContext.params;
}

/**
 * Navigate within the current pane context
 */
export function usePaneNavigate() {
  const paneContext = usePaneRouterContext();
  return paneContext.navigate;
}

/**
 * Check if a specific pane is currently active/open
 */
export function useIsPaneOpen(pane: PaneKey): boolean {
  const location = useLocation();
  const isPaneOpen = !!location.state?.[pane]?.pathname;

  return isPaneOpen;
}

/**
 * Extract parameters from a path using a route pattern
 */
function extractParamsFromPath(
  pathname: string,
  pattern: string,
): Record<string, string> {
  const match = matchPath({ path: pattern, end: true }, pathname);
  const params = match?.params || {};

  // Convert params to ensure all values are strings (not undefined)
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(params)) {
    if (typeof value === "string") {
      result[key] = value;
    }
  }

  return result;
}

const MAIN_ROUTES_WITH_PARAMS = [
  ROUTES.CANVAS.DETAIL,
  ROUTES.CHAT.DETAIL,
  ROUTES.WORKSPACES.DETAIL,
  ROUTES.FILE_RECORDS.DETAIL,
];

/**
 * Get the current route params from React Router (main route params).
 *
 * Derived from the location rather than route-tree position: right-pane
 * content renders as a sibling of the main `<Routes>`, where `useParams()`
 * only sees the outer `path="*"` match.
 */
export function useMainRouteParams(): Readonly<Params<string>> {
  const routeParams = useParams();
  const { pathname } = useLocation();

  for (const pattern of MAIN_ROUTES_WITH_PARAMS) {
    const match = matchPath({ path: pattern, end: true }, pathname);
    if (match) {
      return { ...routeParams, ...match.params };
    }
  }

  return routeParams;
}

/**
 * Close a specific pane by removing it from location state
 */
export function useClosePane() {
  const navigate = useNavigate();
  const location = useLocation();

  return useCallback(
    (pane: PaneKey) => {
      const currentState = (location.state as LocationState) || {};
      if (currentState[pane]?.pathname) {
        trackPaneClosed({
          paneType: paneTypeFor(currentState[pane].pathname),
          method: "close",
        });
      }
      const newState = { ...currentState };
      delete newState[pane];
      delete newState.rightPaneHistory;

      navigate(location.pathname + location.search, {
        replace: true,
        state: newState,
      });
    },
    [navigate, location],
  );
}

/**
 * Step back one level in the right pane, or close the pane when at the root.
 */
export function usePaneGoBack() {
  const navigate = useNavigate();
  const location = useLocation();
  const closePane = useClosePane();

  return useCallback(() => {
    const currentState = (location.state as LocationState) || {};
    const history = currentState.rightPaneHistory ?? [];

    if (history.length > 0) {
      const previous = history[history.length - 1];
      const newHistory = history.slice(0, -1);

      trackPaneOpened({
        paneType: paneTypeFor(previous.pathname),
        method: "back",
        layout: "pane",
      });
      navigate(location.pathname + location.search, {
        replace: true,
        state: {
          ...currentState,
          rightPane: previous,
          rightPaneHistory: newHistory.length > 0 ? newHistory : undefined,
        },
      });
      return;
    }

    closePane("rightPane");
  }, [navigate, location, closePane]);
}
